import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import customLogo from '@/assets/favicon-removebg-preview.png';

interface AuthLayoutProps {
  children: React.ReactNode;
  /** Link shown top-right, e.g. "Don't have an account? Sign up" */
  switchHref?: string;
  switchLabel?: string;
  switchLinkText?: string;
}

const slides = [
  {
    title: "Create smarter forms.",
    highlight: "Turn responses into action.",
    description: "Build forms with AI and automatically turn responses into actionable data."
  },
  {
    title: "Intelligent CRM",
    highlight: "built right in.",
    description: "Every submission instantly becomes an organised contact record."
  },
  {
    title: "Analyze and",
    highlight: "understand.",
    description: "Surface patterns and trends across all your form submissions instantly."
  }
];

const AuthLayout = ({ children, switchHref, switchLabel, switchLinkText }: AuthLayoutProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen flex w-full font-sans">
      {/* ── Left panel ── */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0D0D3A] flex-col items-center justify-between p-12 relative overflow-hidden">
        {/* ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-primary/15 blur-[120px]" />
        </div>

        {/* top spacer */}
        <div />

        {/* Centre content */}
        <div className="relative z-10 flex flex-col items-center text-center gap-8 w-full max-w-[380px]">
          {/* App icon */}
          <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shadow-xl">
            <img src={customLogo} alt="Bravax Forms" className="h-10 w-10 object-contain" />
          </div>

          <div className="relative w-full h-[140px]">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-700 ease-in-out flex flex-col items-center ${
                  index === currentSlide 
                    ? 'opacity-100 translate-x-0' 
                    : index < currentSlide 
                      ? 'opacity-0 -translate-x-8 pointer-events-none' 
                      : 'opacity-0 translate-x-8 pointer-events-none'
                }`}
              >
                <h2 className="text-3xl font-display text-white leading-tight tracking-normal mb-4">
                  {slide.title}<br />
                  <span className="text-white/50 italic">{slide.highlight}</span>
                </h2>
                <p className="text-sm text-white/50 leading-relaxed max-w-[320px]">
                  {slide.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination dots */}
        <div className="relative z-10 flex items-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-primary w-4' : 'bg-white/20 hover:bg-white/40'}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 bg-background flex flex-col relative">
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 pt-8 pb-4 z-10">
          {/* Back button */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors group"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            <span className="font-medium">Back to home</span>
          </Link>

          {/* Switch link */}
          {switchHref && (
            <p className="text-sm text-muted-foreground">
              {switchLabel}{' '}
              <Link to={switchHref} className="text-white font-medium hover:text-primary transition-colors underline underline-offset-2">
                {switchLinkText}
              </Link>
            </p>
          )}
        </div>

        {/* Form — vertically centred */}
        <div className="flex-1 flex items-center justify-center px-6 py-20 overflow-y-auto">
          <div className="w-full max-w-sm mt-8 lg:mt-0">
            {children}
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-8 pb-8 pt-4 bg-background/80 backdrop-blur-sm z-10">
          <span className="text-xs text-muted-foreground">© 2026 Bravax Forms</span>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <Link to="#" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-white transition-colors">Support</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
